import React, { DetailedHTMLProps, FC, ReactElement, useRef } from 'react';

import classNames from 'classnames';

import styles from './menu.module.scss';

interface IMenuProps
  extends DetailedHTMLProps<React.HTMLAttributes<HTMLUListElement>, HTMLUListElement> {
  enhanced?: boolean;
}

const MenuList: FC<IMenuProps> = (props): ReactElement => {
  const {
    children,
    className,
    onKeyDown,
    onFocus,
    enhanced = false,
    ...restProps
  } = props;
  const search = useRef({ text: '', at: 0 });

  const menuClassNames = classNames(styles.menu, className);

  return (
    <ul
      className={menuClassNames}
      role="menu"
      {...restProps}
      onFocus={event => {
        onFocus?.(event);
        if (enhanced) {
          event.currentTarget
            .querySelectorAll<HTMLElement>('[role^="menuitem"]')
            .forEach(control => {
              control.tabIndex = control === document.activeElement ? 0 : -1;
            });
        }
      }}
      onKeyDown={event => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        const controls = Array.from(
          event.currentTarget.querySelectorAll<HTMLElement>(
            enhanced
              ? '[role^="menuitem"]:not(:disabled):not([aria-disabled="true"])'
              : 'button:not(:disabled):not([aria-disabled="true"]),a[href]:not([aria-disabled="true"]),[role="menuitem"][tabindex]:not([aria-disabled="true"]):not(:disabled)',
          ),
        ).filter(
          control =>
            !enhanced ||
            (!control.closest('[hidden],[inert],[aria-hidden="true"]') &&
              getComputedStyle(control).display !== 'none' &&
              getComputedStyle(control).visibility !== 'hidden'),
        );
        if (!controls.length) return;
        const current = controls.indexOf(document.activeElement as HTMLElement);
        let next: number | undefined;
        if (event.key === 'ArrowDown') next = (current + 1) % controls.length;
        if (event.key === 'ArrowUp')
          next =
            current < 0
              ? controls.length - 1
              : (current - 1 + controls.length) % controls.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = controls.length - 1;
        if (
          enhanced &&
          event.key.length === 1 &&
          event.key !== ' ' &&
          !event.ctrlKey &&
          !event.altKey &&
          !event.metaKey
        ) {
          const now = Date.now();
          const SEARCH_TIMEOUT_MS = 500;
          search.current.text =
            now - search.current.at > SEARCH_TIMEOUT_MS
              ? event.key
              : search.current.text + event.key;
          search.current.at = now;
          const query = search.current.text.toLocaleLowerCase();
          const prefix = Array.from(query).every(character => character === query[0])
            ? query[0]
            : query;
          const match = controls.findIndex((_, offset) => {
            const control = controls[(current + 1 + offset) % controls.length];
            return (control.getAttribute('aria-label') ?? control.textContent ?? '')
              .trim()
              .toLocaleLowerCase()
              .startsWith(prefix);
          });
          if (match >= 0) next = (current + 1 + match) % controls.length;
        }
        if (next !== undefined) {
          event.preventDefault();
          controls[next]?.focus();
        }
      }}
    >
      {children}
    </ul>
  );
};

export { MenuList };
